import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTransferFlow } from '@/hooks/use-transfer-flow';
import { useTransferState } from '@/hooks/use-transfer-state';
import { useTransferValidation } from '@/hooks/use-transfer-validation';
import { useTransferJobs } from '@/hooks/use-transfer-jobs';
import { useTransferStatus } from '@/hooks/use-transfer-status';
import {
  type CrossChainRoute,
  routeInformationQueryOptions,
  userTransferStatusQueryOptions,
} from '@/lib/query-options';
import MingcuteTransferVerticalLine from '~icons/mingcute/transfer-vertical-line';
import { ChainTokenInput } from './chain-token-input';
import { ReceiverAddressInput } from './receiver-address-input';
import { TransferConfirmDialog } from './transfer-confirm-dialog';
import { TransferInfo } from './transfer-info';
import { TransferProgressDialog } from './transfer-progress-dialog';
import { TransferringTransfer } from './transferring';

export const TransferCardContent = memo(function TransferCardContent({
  crossChainRoutes,
}: {
  crossChainRoutes: [CrossChainRoute, ...CrossChainRoute[]];
}) {
  const {
    route,
    setRoute,
    fromAmount,
    setFromAmount,
    toAmount,
    receiver,
    setReceiver,
    confirmOpen,
    setConfirmOpen,
    progressOpen,
    setProgressOpen,
    jobId,
    setJobId,
    fromAddress,
    fromAmountBN,
    validUserId,
  } = useTransferState(crossChainRoutes);

  const { data: routeInfo, isFetching: isRouteInfoFetching } = useQuery(
    routeInformationQueryOptions(route.fromChain, route.toChain),
  );

  const { error } = useTransferValidation({
    fromAmountBN,
    receiver,
    minAmount: routeInfo?.limits.min,
    maxAmount: routeInfo?.limits.max,
    isRouteInfoFetching,
    fromAddress,
    toChain: route.toChain,
    fromChain: route.fromChain,
  });

  const transfer = useTransferFlow();

  const { createJob, markJobAsCompleted, markJobAsFailed } = useTransferJobs();

  const handleStatusChange = useCallback(
    (status: any) => {
      if (status.status === 'completed') {
        markJobAsCompleted(status.jobId);
      } else if (status.status === 'failed') {
        markJobAsFailed(status.jobId, status.error);
      }
    },
    [markJobAsCompleted, markJobAsFailed],
  );

  const { data: transferStatus } = useTransferStatus(jobId, {
    enabled: progressOpen,
    onStatusChange: handleStatusChange,
  });

  const { data: userTransferStatus } = useQuery(
    userTransferStatusQueryOptions(validUserId),
  );

  const hasPendingTransfer = useMemo(() => {
    return (
      userTransferStatus && userTransferStatus.adminApprovalStatus === 'pending'
    );
  }, [userTransferStatus]);

  return (
    <div className="flex flex-col">
      <ChainTokenInput
        dir="from"
        route={route}
        token={'USDT'}
        readOnly={transfer.isPending}
        amount={fromAmount}
        decimals={routeInfo?.fromChain.usdtDecimals}
        address={fromAddress}
        routes={crossChainRoutes}
        minAmount={routeInfo?.limits.min}
        maxAmount={routeInfo?.limits.max}
        onChange={(route) => setRoute(route)}
        onAmountChange={setFromAmount}
      />

      <div className="mx-auto -my-2 z-10 bg-background ring-1 ring-foreground/10 shadow-md rounded-full p-1">
        <MingcuteTransferVerticalLine className="size-4" />
      </div>

      <ChainTokenInput
        dir="to"
        route={route}
        token={'USDT'}
        readOnly={transfer.isPending}
        amount={toAmount}
        routes={[]}
        decimals={routeInfo?.toChain.usdtDecimals}
        address={receiver}
        onChange={() => {}}
      />

      <ReceiverAddressInput
        value={receiver}
        readOnly={transfer.isPending}
        onChange={setReceiver}
      />

      {hasPendingTransfer && (
        <TransferringTransfer
          className="mt-4"
          userTransferStatus={userTransferStatus}
        />
      )}

      {!hasPendingTransfer && (
        <TransferInfo
          receiver={receiver}
          fromAddress={fromAddress || ''}
          className="mt-4"
          amount={fromAmount}
          recive={toAmount}
          route={route}
        />
      )}

      {!hasPendingTransfer && (
        <TransferConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          isPending={transfer.isPending}
          error={error}
          isRouteInfoFetching={isRouteInfoFetching}
          receiver={receiver}
          fromAddress={fromAddress || ''}
          fromAmount={fromAmount}
          toAmount={toAmount}
          route={route}
          onConfirm={() => {
            transfer.mutate(
              {
                fromAddress,
                fromAmount,
                receiver,
                fromChain: route.fromChain,
                toChain: route.toChain,
                defaultContractAddress: route.defaultContractAddress,
              },
              {
                onSuccess: (data) => {
                  setJobId(data.jobId);
                  setConfirmOpen(false);
                  setProgressOpen(true);

                  // Create job record with the real jobId
                  createJob(
                    {
                      jobId: data.jobId,
                      transferId: data.jobId, // Use jobId as transferId for now
                      fromChain: route.fromChain,
                      toChain: route.toChain,
                      amount: fromAmount,
                      fromAddress: fromAddress || '',
                      toAddress: receiver,
                    },
                    'active',
                  );
                },
              },
            );
          }}
        />
      )}

      <TransferProgressDialog
        open={progressOpen}
        onOpenChange={setProgressOpen}
        receiver={receiver}
        fromAddress={fromAddress || ''}
        fromAmount={fromAmount}
        toAmount={toAmount}
        route={route}
        progress={transferStatus?.progress ?? 0}
        status={transferStatus?.status ?? 'pending'}
        error={transferStatus?.error}
        result={transferStatus?.result}
      />
    </div>
  );
});


